
import { useState, useEffect } from 'react';

const HeroSection = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    roofIssue: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams({
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        roofIssue: formData.roofIssue
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d5l6um5porqtj2nscjq0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setSubmitStatus('success');
        window.location.href = '/thank-you';
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center pt-16 sm:pt-20">
      {/* Background Image with Stronger Overlay for Better Text Contrast */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/223cfd00f4406b43c7c307e70268ad26.jpg"
          alt="Professional Roof Installation"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/70 to-black/50 sm:from-black/70 sm:via-black/50 sm:to-black/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 lg:py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Content */}
          <div className={`text-white transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div className="mb-4 sm:mb-6">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold leading-tight mb-3 sm:mb-4 font-poppins text-white drop-shadow-lg">
                BE YOUR OWN ROOFER
              </h1>
              <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold leading-tight text-[#FF9500] drop-shadow-md">
                PUT THOUSANDS BACK IN YOUR POCKET
              </p>
            </div>
            
            <p className="text-lg sm:text-xl md:text-2xl font-semibold text-white mb-4 sm:mb-6 leading-relaxed drop-shadow-md">
              Pay only for materials + labor. No expensive middleman. No hidden margins. Just transparent, flat-fee pricing.
            </p>

            <p className="text-base sm:text-lg text-white font-medium mb-6 sm:mb-8 leading-relaxed drop-shadow-sm">
              Traditional roofing companies mark up the price to pay for expensive overhead. We connect you directly to quality materials and skilled installers at wholesale prices. You keep the savings — thousands of dollars that stay in YOUR pocket, not theirs.
            </p>

            {/* Savings Highlight */}
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 transform hover:scale-105 transition-all duration-300 hover:shadow-2xl">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 flex items-center justify-center bg-[#4A90E2] rounded-full flex-shrink-0 animate-bounce">
                  <i className="ri-money-dollar-circle-line text-xl sm:text-2xl lg:text-3xl text-white"></i>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#2B5BA6] leading-tight">Keep anywhere from $2,000 - $10,000 dollars in your pocket.</p>
                  <p className="text-xs sm:text-sm text-gray-700 font-medium mt-1">No middleman markup — just materials + labor + small flat fee</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 grid grid-cols-1 gap-3 text-xs sm:text-sm">
                <div className="transform hover:translate-x-1 transition-transform duration-200">
                  <p className="text-gray-600 font-medium">Traditional companies charge:</p>
                  <p className="font-bold text-gray-900">30-50% markup on everything</p>
                </div>
                <div className="transform hover:translate-x-1 transition-transform duration-200">
                  <p className="text-gray-600 font-medium">You pay with us:</p>
                  <p className="font-bold text-gray-900">Materials + Labor + Transparent Flat Fee</p>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {[
                { icon: 'ri-flashlight-line', text: 'Virtual Estimate in 24hrs', delay: '100' },
                { icon: 'ri-price-tag-3-line', text: 'Transparent Flat Fee', delay: '200' },
                { icon: 'ri-shield-check-line', text: 'No Hidden Margins', delay: '300' },
                { icon: 'ri-hand-coin-line', text: 'Keep Your Savings', delay: '400' }
              ].map((badge, index) => (
                <div 
                  key={index}
                  className="text-center transform hover:scale-110 transition-all duration-300 hover:-translate-y-1 p-2 bg-black/30 rounded-xl backdrop-blur-sm"
                  style={{ animationDelay: `${badge.delay}ms` }}
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mx-auto mb-2">
                    <i className={`${badge.icon} text-2xl sm:text-3xl lg:text-4xl text-[#FF9500]`}></i>
                  </div>
                  <p className="text-xs sm:text-sm font-semibold leading-tight text-white drop-shadow-md">{badge.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Form Card */}
          <div className={`bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-5 sm:p-8 lg:p-10 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 font-poppins">
              Get Your Transparent Pricing Quote
            </h2>
            <p className="text-sm text-gray-600 mb-4">See exactly what you pay for materials, labor, and our flat fee. No hidden markups. Quote in 24 hours.</p>
            <form onSubmit={handleSubmit} data-readdy-form id="homepage-lead-form">
              <div className="space-y-3 sm:space-y-4">
                <div className="transform transition-all duration-300 hover:scale-[1.02]">
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    required
                    className="w-full px-4 py-3 sm:py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent outline-none transition-all text-base min-h-[48px] focus:shadow-lg"
                  />
                </div>
                <div className="transform transition-all duration-300 hover:scale-[1.02]">
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 sm:py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent outline-none transition-all text-base min-h-[48px] focus:shadow-lg"
                  />
                </div>
                <div className="transform transition-all duration-300 hover:scale-[1.02]">
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    className="w-full px-4 py-3 sm:py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent outline-none transition-all text-base min-h-[48px] focus:shadow-lg"
                  />
                </div>
                <div className="relative transform transition-all duration-300 hover:scale-[1.02]">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none">
                    <i className="ri-map-pin-line text-gray-400 text-lg"></i>
                  </div>
                  <input
                    type="text"
                    name="address"
                    placeholder="Property Address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    className="w-full pl-12 pr-4 py-3 sm:py-3.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent outline-none transition-all text-base min-h-[48px] focus:shadow-lg"
                  />
                </div>
                <div className="transform transition-all duration-300 hover:scale-[1.02]">
                  <textarea
                    name="roofIssue"
                    placeholder="What do you need? (roof replacement, repairs, storm damage, etc.)"
                    value={formData.roofIssue}
                    onChange={(e) => setFormData({ ...formData, roofIssue: e.target.value })}
                    required
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#4A90E2] focus:border-transparent outline-none transition-all resize-none text-base focus:shadow-lg"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.roofIssue.length}/500 characters</p>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#2B5BA6] text-white py-4 rounded-xl font-semibold hover:bg-[#234a8a] active:bg-[#1e3d73] transition-all duration-300 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer min-h-[48px] text-base transform hover:scale-[1.02] hover:shadow-xl active:scale-[0.98]"
                >
                  <span>{isSubmitting ? 'Submitting...' : 'See My Transparent Quote'}</span>
                  <i className={`ri-arrow-right-line text-lg transition-transform duration-300 ${isSubmitting ? '' : 'group-hover:translate-x-1'}`}></i>
                </button>
              </div>
              {submitStatus === 'error' && (
                <p className="mt-4 text-red-600 text-sm text-center font-medium animate-shake">
                  Something went wrong. Please try again.
                </p>
              )}
              <p className="text-xs text-gray-500 text-center mt-4">
                No obligation. Completely free. See the breakdown in 24 hours.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
